/**
 * Console pattern: general/Runtime — 运行时监控.
 *
 * Spec §5.5. 顶部 service tab + 4 进程健康卡 + 告警卡 + 日志区
 * (filter / level / dedup / 时间分隔符 / 终端样式) + Debug Snapshot 折叠.
 *
 * 静态原型：使用 useState 切 service tab 和 log filter；日志是写死的代表性
 * mock 行（按 spec §1.4 的 4 进程 caller / responder / relay / supervisor）。
 */

import { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Bug,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Filter,
  RefreshCw,
  Terminal,
} from "lucide-react";

import {
  ConsolePageHeader,
  ConsoleCard,
  ConsoleButton,
} from "../shells/console-shell";
import { cn } from "@/app/components/ui/utils";

type ServiceKey = "caller" | "responder" | "relay";
type Level = "all" | "info" | "warn" | "error";

const SERVICE_TABS: { key: ServiceKey; label: string; tone: string }[] = [
  { key: "caller", label: "Caller", tone: "var(--brand-teal)" },
  { key: "responder", label: "Responder", tone: "var(--brand-orange)" },
  { key: "relay", label: "Relay", tone: "var(--brand-purple)" },
];

type ProcInfo = {
  name: string;
  pid: number | null;
  uptime: string;
  state: "running" | "stopped" | "crashed" | "starting";
  lastError?: string;
  doc: string;
};

const PROC_INFO: Record<ServiceKey, ProcInfo[]> = {
  caller: [
    {
      name: "caller-runtime",
      pid: 49210,
      uptime: "3h 12m",
      state: "running",
      doc: "MCP server，Agent 和这里说话；解析意图后向本机其它进程下单。",
    },
    {
      name: "caller-supervisor",
      pid: 49202,
      uptime: "3h 12m",
      state: "running",
      doc: "看护 caller-runtime；崩了自动 backoff 重启。",
    },
  ],
  responder: [
    {
      name: "responder-runtime",
      pid: 49314,
      uptime: "1h 04m",
      state: "running",
      doc: "本机 Hotline 容器；执行你发布的能力代码。",
    },
    {
      name: "responder-supervisor",
      pid: 49301,
      uptime: "1h 05m",
      state: "running",
      doc: "看护 responder-runtime；按 hotline 配置启停子进程。",
    },
  ],
  relay: [
    {
      name: "relay-client",
      pid: null,
      uptime: "—",
      state: "stopped",
      doc: "可选。本地未启用 Relay 时不跑；切到 relay_http 模式后由 supervisor 拉起。",
    },
  ],
};

type LogLine = {
  ts: string;
  level: "info" | "warn" | "error";
  source: string;
  msg: string;
  /** Aggregated repeats per spec §5.5 dedup. */
  count?: number;
};

const LOGS: Record<ServiceKey, LogLine[]> = {
  caller: [
    { ts: "14:08:44", level: "info", source: "caller-runtime", msg: "GET /status 200" },
    { ts: "14:08:42", level: "info", source: "caller-runtime", msg: "tool_call accepted (call_id=clrun_8c2…)" },
    { ts: "14:08:39", level: "warn", source: "caller-runtime", msg: "approval timeout 30s, prompting user", count: 2 },
    { ts: "14:08:11", level: "info", source: "caller-runtime", msg: "responder catalog refreshed (n=14)" },
    { ts: "14:07:58", level: "error", source: "caller-runtime", msg: "platform.fetchManifest: ECONNREFUSED 127.0.0.1:8092" },
    { ts: "14:07:30", level: "info", source: "caller-supervisor", msg: "child started pid=49210" },
  ],
  responder: [
    { ts: "14:08:43", level: "info", source: "responder-runtime", msg: "exec ok hotline=local.workspace-summary.v1 dur=312ms" },
    { ts: "14:08:21", level: "warn", source: "responder-runtime", msg: "soft_timeout (3s) on calendar.create-event; continuing" },
    { ts: "14:07:51", level: "info", source: "responder-runtime", msg: "loaded hotline draft hotline_id=ops.notify@v1" },
    { ts: "14:07:30", level: "info", source: "responder-supervisor", msg: "child started pid=49314" },
  ],
  relay: [
    { ts: "14:00:00", level: "info", source: "supervisor", msg: "transport=local · relay client not started" },
  ],
};

const LEVEL_FG: Record<LogLine["level"], string> = {
  info: "text-[var(--brand-muted)]",
  warn: "text-[var(--c-status-warn-fg)]",
  error: "text-[var(--c-status-error-fg)]",
};

const STATE_TONE: Record<ProcInfo["state"], string> = {
  running: "c-status-success",
  stopped: "c-status-warn",
  crashed: "c-status-error",
  starting: "c-status-info",
};

export function ConsolePageRuntime() {
  const [service, setService] = useState<ServiceKey>("caller");
  const [level, setLevel] = useState<Level>("all");
  const [filter, setFilter] = useState("");
  const [snapshotOpen, setSnapshotOpen] = useState(false);

  const procs = PROC_INFO[service];
  const allLogs = LOGS[service];

  const visibleLogs = useMemo(() => {
    return allLogs.filter((l) => {
      if (level !== "all" && l.level !== level) return false;
      if (filter && !`${l.source} ${l.msg}`.toLowerCase().includes(filter.toLowerCase()))
        return false;
      return true;
    });
  }, [allLogs, level, filter]);

  return (
    <>
      <ConsolePageHeader
        kicker="GENERAL · 运行时"
        title="进程健康 · 日志 · 故障排查"
        description="按 service 切分。每 5s silent 拉一次 /runtime/snapshot 与 /runtime/logs?service=…，原型展示静态快照。"
        actions={
          <ConsoleButton variant="ghost">
            <RefreshCw className="h-3.5 w-3.5" />
            刷新
            <span className="text-[10px] text-[var(--brand-muted)] ml-1">(5s 自动)</span>
          </ConsoleButton>
        }
      />

      {/* Service tabs */}
      <div className="mb-5 flex items-center gap-1 border-b" style={{ borderColor: "var(--border)" }}>
        {SERVICE_TABS.map((t) => {
          const active = service === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setService(t.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 -mb-px text-[13px] font-semibold border-b-2",
                active
                  ? "border-[var(--brand-ink)] text-[var(--ink)]"
                  : "border-transparent text-[var(--brand-muted)] hover:text-[var(--ink)]",
              )}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: t.tone }}
              />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* 3 health cards (or fewer per service) */}
      <div className={cn("grid gap-3 mb-5", procs.length === 1 ? "grid-cols-1" : "grid-cols-2")}>
        {procs.map((p) => (
          <ConsoleCard key={p.name} padded={false}>
            <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2 min-w-0">
                <CircleDot
                  className={cn(
                    "h-3.5 w-3.5 shrink-0",
                    p.state === "running" && "text-[var(--c-status-success-fg)]",
                    p.state === "stopped" && "text-[var(--c-status-warn-fg)]",
                    p.state === "crashed" && "text-[var(--c-status-error-fg)]",
                    p.state === "starting" && "text-[var(--c-status-info-fg)]",
                  )}
                  strokeWidth={2.5}
                />
                <p className="font-mono text-[12.5px] truncate">{p.name}</p>
              </div>
              <span className={cn("c-status", STATE_TONE[p.state])}>{p.state}</span>
            </div>
            <div className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              <Stat label="PID" value={p.pid !== null ? String(p.pid) : "—"} mono />
              <Stat label="Uptime" value={p.uptime} />
              <div className="col-span-2">
                <p className="text-[10.5px] text-[var(--brand-muted)] uppercase tracking-wider mb-0.5">
                  作用
                </p>
                <p className="text-[11.5px] text-[var(--ink)]/80 leading-snug">{p.doc}</p>
              </div>
              {p.lastError && (
                <div className="col-span-2 mt-1 p-2 bg-[var(--c-status-error-bg)] text-[11.5px]" style={{ borderRadius: "var(--c-radius-sm)" }}>
                  <span className="font-semibold text-[var(--c-status-error-fg)]">last_error · </span>
                  <span className="font-mono text-[var(--ink)]">{p.lastError}</span>
                </div>
              )}
            </div>
          </ConsoleCard>
        ))}
      </div>

      {/* Conditional alert (responder service only — demo dedup banner) */}
      {service === "responder" && (
        <div
          className="mb-5 p-3 border-l-[3px] border-l-[var(--brand-yellow)] bg-[color-mix(in_oklab,var(--brand-yellow)_8%,transparent)] flex items-start gap-3"
          style={{ borderRadius: "var(--c-radius-sm)" }}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 text-[var(--c-status-warn-fg)] shrink-0" strokeWidth={2.5} />
          <div className="flex-1">
            <p className="text-[12.5px] font-semibold text-[var(--ink)]">
              过去 5 分钟内 calendar.create-event 触发 soft_timeout × 3
            </p>
            <p className="text-[11.5px] text-[var(--brand-muted)] mt-0.5">
              hotline 配置的 soft_timeout=3s 偏紧。建议在 Hotline 管理调到 5s，或检查上游 API 延迟。
            </p>
          </div>
          <button className="text-[11px] font-medium text-[var(--brand-muted)] hover:text-[var(--ink)] shrink-0">
            消除
          </button>
        </div>
      )}

      {/* Logs */}
      <ConsoleCard padded={false} className="mb-5 overflow-hidden">
        <div className="px-4 py-2.5 flex items-center gap-3 border-b" style={{ borderColor: "var(--border)" }}>
          <Terminal className="h-4 w-4" strokeWidth={2.5} />
          <p className="text-[13px] font-semibold">日志 · 实时</p>
          <span className="text-[10.5px] text-[var(--brand-muted)] font-mono">
            tail -f {service}.log
          </span>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Filter className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]" strokeWidth={2.5} />
              <input
                type="text"
                placeholder="搜本字段"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-7 pr-2 py-1 text-[11.5px] border bg-white w-40 font-mono"
                style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
              />
            </div>
            <div className="flex border" style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}>
              {(["all", "info", "warn", "error"] as Level[]).map((lv) => (
                <button
                  key={lv}
                  onClick={() => setLevel(lv)}
                  className={cn(
                    "px-2 py-1 text-[10.5px] font-semibold uppercase tracking-wider",
                    level === lv ? "bg-[var(--brand-ink)] text-white" : "text-[var(--brand-muted)] hover:text-[var(--ink)]",
                  )}
                >
                  {lv}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] text-[#e8e8e0] font-mono text-[11.5px] py-2 max-h-[280px] overflow-y-auto">
          {/* date divider */}
          <div className="px-4 py-1 text-[#8a8a82] text-[10px] uppercase tracking-wider">
            ─── today · 2026-04-21 ───
          </div>
          {visibleLogs.length === 0 && (
            <div className="px-4 py-3 text-[#8a8a82]">无匹配日志。</div>
          )}
          {visibleLogs.map((l, i) => (
            <div key={i} className="px-4 py-0.5 flex items-start gap-3 hover:bg-white/5">
              <span className="text-[#8a8a82] shrink-0 w-16">{l.ts}</span>
              <span
                className={cn(
                  "shrink-0 w-12 uppercase font-semibold",
                  l.level === "info" && "text-[#7dd3fc]",
                  l.level === "warn" && "text-[#fbbf24]",
                  l.level === "error" && "text-[#f87171]",
                )}
              >
                {l.level}
              </span>
              <span className="text-[#a8a8a0] shrink-0 w-44 truncate">{l.source}</span>
              <span className="flex-1 break-all">{l.msg}</span>
              {l.count && l.count > 1 && (
                <span className="shrink-0 px-1.5 py-px bg-white/10 text-[10px] tabular-nums" style={{ borderRadius: "var(--c-radius-pill)" }}>
                  ×{l.count}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className={cn("px-4 py-1.5 text-[10.5px] flex items-center justify-between border-t", LEVEL_FG.info)} style={{ borderColor: "var(--border)" }}>
          <span>显示 {visibleLogs.length} / {allLogs.length} 行 · 重复行已聚合</span>
          <span>POST /runtime/logs?service={service}&follow=true</span>
        </div>
      </ConsoleCard>

      {/* Debug snapshot collapsible */}
      <ConsoleCard padded={false}>
        <button
          onClick={() => setSnapshotOpen(!snapshotOpen)}
          className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-[var(--brand-secondary)]/30"
        >
          {snapshotOpen ? (
            <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          )}
          <Bug className="h-3.5 w-3.5" strokeWidth={2.5} />
          <span className="text-[13px] font-semibold">Debug Snapshot</span>
          <span className="text-[10.5px] text-[var(--brand-muted)] font-mono ml-auto">
            POST /runtime/snapshot
          </span>
        </button>
        {snapshotOpen && (
          <pre className="px-4 pb-4 text-[11.5px] font-mono text-[var(--ink)] m-0 overflow-x-auto leading-relaxed">
{`{
  "service": "${service}",
  "captured_at": "2026-04-21T14:08:44Z",
  "version": "0.7.4",
  "config_hash": "sha256:fb2a…",
  "transport": "local",
  "active_calls": ${service === "caller" ? "1" : "0"},
  "active_hotlines": ${service === "responder" ? "3" : "0"},
  "memory": { "rss_mb": 142, "heap_used_mb": 88 }
}`}
          </pre>
        )}
      </ConsoleCard>
    </>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10.5px] text-[var(--brand-muted)] uppercase tracking-wider">{label}</p>
      <p className={cn("text-[13px] text-[var(--ink)]", mono && "font-mono")}>{value}</p>
    </div>
  );
}
